import React, {useState} from 'react';
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {invalidate, prefetchNearbyPages} from "services/query-client";
import {TemplateIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {ExhibitType} from "types/ExhibitType";
import ConfirmModal from "components/ConfirmModal";
import {useOperator} from "context/OperatorContext";
import Icon from "components/Icon";
import {useAuth} from "context/AuthContext";
import ExhibitTypeContextMenu from "components/menus/ExhibitTypeContextMenu";

// Component ----

function ExhibitTypeList() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['exhibit-types.all', {page}],
    () => ngage.exhibitTypes.all(page),
    {keepPreviousData: true, enabled: auth.can('exhibit-types:read')}
  )

  const [deletingExhibitType, setDeletingExhibitType] = useState<ExhibitType | null>(null);
  const {mutateAsync: deleteExhibitType} = useMutation(ngage.exhibitTypes.destroy)

  // Prepare action handlers
  async function handleDeleteExhibitType(exhibitType: ExhibitType) {
    if (exhibitType.id) {
      try {
        await deleteExhibitType(exhibitType.id)

        setDeletingExhibitType(null)
        notify("success", `${termFor('Exhibit Type')} Deleted`, `${exhibitType.name} has been permanently removed`)

        return invalidate('exhibit-types.all')
      } catch (error) {
        setDeletingExhibitType(null)
        notify("error", `${termFor('Exhibit Type')} Not Deleted`, `${exhibitType.name} could not be removed`)
      }
    }
  }

  // Prefetch next and previous pages
  prefetchNearbyPages('exhibit-types.all', page, ngage.exhibitTypes.all)

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('exhibit-types:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/exhibit-types/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Exhibit Types')]: '/exhibit-types'
  }

  return (
    <Page
      title={termFor('Exhibit Types')}
      actions={actions}
      breadcrumbs={breadcrumbs}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>{termFor('Exhibits')}</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={5}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <TemplateIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('exhibit types')}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((exhibitType, index) => (
              <TableRow key={exhibitType.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <ExhibitTypeContextMenu
                        exhibitType={exhibitType}
                        onDeleteExhibitType={() => setDeletingExhibitType(exhibitType)}
                        deleteDisabled={Number(exhibitType.exhibit_count) > 0}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <Icon
                        name={exhibitType.icon}
                        className="h-5 w-5 mr-2 text-gray-500"
                      /> {exhibitType.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{truncate(exhibitType.description, 50)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{exhibitType.exhibit_count}</TableData>
                <TableData>{formattedDateTime(exhibitType.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <ExhibitTypeContextMenu
                    exhibitType={exhibitType}
                    onDeleteExhibitType={() => setDeletingExhibitType(exhibitType)}
                    deleteDisabled={Number(exhibitType.exhibit_count) > 0}
                  />
                </TableData>

                {/* Delete modal */}
                <ConfirmModal
                  show={deletingExhibitType === exhibitType}
                  type={ConfirmModal.types.DANGER}
                  title={`Delete ${termFor('Exhibit Type')}`}
                  body={`Are you sure you want to permanently delete '${exhibitType.name}'? This action cannot be undone.`}
                  approveLabel="Delete"
                  onApprove={() => handleDeleteExhibitType(exhibitType)}
                  onCancel={() => setDeletingExhibitType(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default ExhibitTypeList;